import type { InfiniteData, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  moveChannelToTop,
  replaceChannelPreview,
} from '~hooks/useMessaging/messagingChannelsCache';
import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import {
  isListBeingRead,
  reReadPatchedList,
  writePatchedList,
} from '~hooks/useMessaging/reReadPatchedList';
import {
  isChannelFilteredOutOfList,
  MESSAGING_CHANNELS_QUERY_KEY,
} from '~queries/messagingQueryKeys';
import type { MessagingChannelsPage } from '~queries/useMessagingChannelsInfiniteQuery';

/**
 * The list is ordered by most recent message, so a listed conversation is moved to the top in
 * place with its new preview, and only one the list lacks is re-read. Each cached list decides
 * for itself: a conversation on a tab's first page can sit past the loaded pages of another.
 */
export default function useMessagingChannelListBump() {
  const queryClient = useQueryClient();

  const bumpChannelInLists = useCallback(
    (
      channelId: string,
      message: MessagingMessage,
      { shouldReReadListsLacking }: { shouldReReadListsLacking: boolean },
    ) => {
      const channelLists = queryClient.getQueriesData<
        InfiniteData<MessagingChannelsPage>
      >({ queryKey: MESSAGING_CHANNELS_QUERY_KEY });
      const listsLackingChannel: QueryKey[] = [];
      let bumpedChannel: MessagingChannel | undefined;

      for (const [channelListKey, channelList] of channelLists) {
        const reorderedList =
          channelList && moveChannelToTop(channelList, channelId, message);
        if (!reorderedList) {
          listsLackingChannel.push(channelListKey);
          continue;
        }

        bumpedChannel = reorderedList.pages
          .flatMap((page) => page.channels)
          .find((channel) => channel.id === channelId);
        writePatchedList(queryClient, channelListKey, reorderedList);
      }

      if (!shouldReReadListsLacking) return;

      for (const channelListKey of listsLackingChannel) {
        const isChannelFilteredOut =
          bumpedChannel !== undefined &&
          isChannelFilteredOutOfList(channelListKey, bumpedChannel);
        if (isChannelFilteredOut) continue;

        if (isListBeingRead(queryClient, channelListKey)) {
          void reReadPatchedList(queryClient, channelListKey);
        } else {
          void queryClient.invalidateQueries({ queryKey: channelListKey, exact: true });
        }
      }
    },
    [queryClient],
  );

  const replaceChannelPreviewInLists = useCallback(
    (channelId: string, message: MessagingMessage) => {
      const channelLists = queryClient.getQueriesData<
        InfiniteData<MessagingChannelsPage>
      >({ queryKey: MESSAGING_CHANNELS_QUERY_KEY });

      for (const [channelListKey, channelList] of channelLists) {
        const patchedList =
          channelList && replaceChannelPreview(channelList, channelId, message);
        if (!patchedList) continue;

        writePatchedList(queryClient, channelListKey, patchedList);
      }
    },
    [queryClient],
  );

  return { bumpChannelInLists, replaceChannelPreviewInLists };
}
